import React, { useEffect, useState } from 'react';
import { useCertificacion } from '../../hooks/useCertificacion';
import { useCertificacionPresencial } from '../../hooks/useCertificacionPresencial';
import { Button, Spinner, Alert } from 'flowbite-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faDownload, faShieldAlt, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import './CertificacionQR.css';

interface CertificacionQRProps {
  evaluadoId: string;
  nombreEvaluado: string;
  showInDocument?: boolean;
  autoGenerate?: boolean;
  className?: string;
  tipoEvaluacion?: 'linea' | 'presencial';
}

export const CertificacionQR: React.FC<CertificacionQRProps> = ({
  evaluadoId,
  nombreEvaluado,
  showInDocument = false,
  autoGenerate = false,
  className = '',
  tipoEvaluacion = 'linea'
}) => {
  const hookLinea = useCertificacion();
  const hookPresencial = useCertificacionPresencial();
  
  // Seleccionar el hook apropiado según el tipo de evaluación
  const { generarCertificacion, obtenerCertificacion, regenerarCertificacion, descargarQR, loading, error, clearError } = 
    tipoEvaluacion === 'presencial' ? hookPresencial : hookLinea;
  
  const [certificacion, setCertificacion] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const cargarCertificacion = async () => {
      try {
        // Primero intentar obtener certificación existente
        const cert = await obtenerCertificacion(evaluadoId);
        
        if (cert) {
          setCertificacion(cert);
        } else if (autoGenerate) {
          // Si no existe y autoGenerate está habilitado, generar nueva
          const nuevaCert = await generarCertificacion(evaluadoId);
          if (nuevaCert) {
            setCertificacion(nuevaCert);
          }
        }
      } catch (err) {
        console.error('Error cargando certificación:', err);
      }
    };

    if (evaluadoId) {
      cargarCertificacion();
    }
  }, [evaluadoId, autoGenerate, tipoEvaluacion]);

  const handleGenerarCertificacion = async () => {
    clearError();
    const nuevaCert = await generarCertificacion(evaluadoId);
    if (nuevaCert) {
      setCertificacion(nuevaCert);
    }
  };

  const handleRegenerarCertificacion = async () => {
    if (!window.confirm('¿Está seguro de regenerar la certificación? La certificación actual será invalidada.')) {
      return;
    }
    
    clearError();
    const nuevaCert = await regenerarCertificacion(evaluadoId);
    if (nuevaCert) {
      setCertificacion(nuevaCert);
    }
  };

  const handleDescargarQR = () => {
    if (certificacion?.qr_code) {
      descargarQR(certificacion.qr_code, nombreEvaluado);
    }
  };

  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTipoEvaluacionLabel = () => {
    return tipoEvaluacion === 'presencial' ? 'Presencial' : 'En Línea';
  };

  // Vista para mostrar en el documento (compacta)
  if (showInDocument && certificacion) {
    return (
      <div className={`certificacion-documento ${className}`}>
        <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center space-x-4">
            <div className="qr-container">
              <img 
                src={`data:image/png;base64,${certificacion.qr_code}`}
                alt="Código QR de Certificación"
                className="w-20 h-20 border border-gray-300 rounded"
              />
            </div>
            <div className="certificacion-info">
              <div className="flex items-center space-x-2 mb-1">
                <FontAwesomeIcon icon={faShieldAlt} className="text-green-600" />
                <span className="text-sm font-semibold text-gray-800">
                  Documento Certificado ({getTipoEvaluacionLabel()})
                </span>
              </div>
              <p className="text-xs text-gray-600">
                Certificación: {certificacion.numero_certificacion}
              </p>
              <p className="text-xs text-gray-600">
                Fecha: {formatearFecha(certificacion.fecha_certificacion)}
              </p>
              <p className="text-xs text-gray-500">
                Escanea el QR para verificar autenticidad
              </p>
            </div>
          </div>
          {certificacion.integridad_valida !== false && (
            <div className="flex items-center text-green-600">
              <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
              <span className="text-xs">Verificado</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Vista completa para gestión
  return (
    <div className={`certificacion-completa ${className}`}>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon={faQrcode} className="text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-800">
                Certificación del Documento ({getTipoEvaluacionLabel()})
              </h3>
            </div>
            {certificacion && (
              <Button
                size="sm"
                color="gray"
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? 'Ocultar' : 'Ver'} Detalles
              </Button>
            )}
          </div>
        </div>

        <div className="p-4">
          {error && (
            <Alert color="failure" className="mb-4">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              {error}
            </Alert>
          )}

          {!certificacion ? (
            <div className="text-center py-8">
              <FontAwesomeIcon icon={faShieldAlt} className="text-gray-400 text-4xl mb-4" />
              <p className="text-gray-600 mb-4">
                Este documento aún no ha sido certificado
              </p>
              <Button
                onClick={handleGenerarCertificacion}
                disabled={loading}
                color="blue"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Generando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
                    Generar Certificación {getTipoEvaluacionLabel()}
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div>
              <div className="flex items-start space-x-6">
                <div className="qr-container">
                  <img 
                    src={`data:image/png;base64,${certificacion.qr_code}`}
                    alt="Código QR de Certificación"
                    className="w-32 h-32 border border-gray-300 rounded-lg shadow-sm"
                  />
                  <Button
                    size="sm"
                    color="gray"
                    onClick={handleDescargarQR}
                    className="w-full mt-2"
                  >
                    <FontAwesomeIcon icon={faDownload} className="mr-2" />
                    Descargar QR
                  </Button>
                  <Button
                    size="sm"
                    color="warning"
                    onClick={handleRegenerarCertificacion}
                    disabled={loading}
                    className="w-full mt-2"
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Regenerando...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
                        Regenerar
                      </>
                    )}
                  </Button>
                </div>

                <div className="flex-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Número de Certificación
                      </label>
                      <p className="text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">
                        {certificacion.numero_certificacion}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fecha de Certificación
                      </label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                        {formatearFecha(certificacion.fecha_certificacion)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL de Verificación
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={certificacion.url_verificacion}
                        readOnly
                        className="flex-1 text-xs text-gray-600 bg-gray-50 border border-gray-300 rounded px-2 py-1"
                      />
                      <Button
                        size="xs"
                        color="gray"
                        onClick={() => window.open(certificacion.url_verificacion, '_blank')}
                      >
                        Verificar
                      </Button>
                    </div>
                  </div>

                  {showDetails && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Módulos Certificados
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {certificacion.modulos_certificados?.map((modulo: string, index: number) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                          >
                            <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                            {modulo}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificacionQR;
